import { Layout } from '@/components/Layout.tsx';
import { Icons } from '@/components/ui.tsx';
import {
  CREDENTIALS,
  HOW_IT_WORKS_CALLOUTS,
  HOW_IT_WORKS_STEPS,
  PRODUCT_PRINCIPLES,
} from '@/data/content.ts';
import { asset, href } from '@/lib/paths.ts';

export function HowItWorks() {
  return (
    <Layout currentPath="/how-it-works">
      {/* Hero Section */}
      <section className="how-hero">
        <div className="container how-hero__content">
          <span className="how-hero__badge fade-in">
            The Proactive Tutoring Workflow
          </span>
          <h1 className="fade-in">How LearnAI Works</h1>
          <p className="how-hero__lede fade-in">
            A teacher-first learning workflow grounded in cognitive science,
            proactive Socratic inquiry, and actionable comprehension data.
          </p>
        </div>
      </section>

      {/* Section 1: Our Solution — The Learning Workflow (Slide 2) */}
      <section className="how-workflow-section" id="solution">
        <div className="container">
          <div className="how-section-header fade-in">
            <span className="how-section-eyebrow">The Learning Cycle</span>
            <h2>Our Solution</h2>
            <p>
              How LearnAI empowers educators, guides students, and preserves
              classroom critical thinking.
            </p>
          </div>

          <div className="how-workflow-grid">
            {/* Left Column: 4-Step Process */}
            <div className="how-steps">
              {HOW_IT_WORKS_STEPS.map((s) => (
                <div className="how-step-card fade-in" key={s.step}>
                  <div className="how-step-number" aria-hidden="true">
                    <span>{s.step}</span>
                  </div>
                  <div className="how-step-content">
                    <h3>{s.title}</h3>
                    <p>{s.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Previews & Key Callouts */}
            <div className="how-visuals">
              {HOW_IT_WORKS_CALLOUTS.map((callout) => (
                <div className="how-visual-card fade-in" key={callout.title}>
                  <div className="how-visual-card__header">
                    <span className="how-visual-tag">
                      {callout.title === 'Proactive vs Reactive'
                        ? 'Active Socratic Learning'
                        : 'Teacher Authority'}
                    </span>
                    <h3>{callout.title}</h3>
                    <p>{callout.description}</p>
                  </div>
                  <div className="how-visual-card__image-box">
                    <img
                      src={asset(callout.image)}
                      alt={callout.imageAlt}
                      width={callout.imageWidth}
                      height={callout.imageHeight}
                      loading="lazy"
                      className="how-visual-card__img"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Principles of Our Product (Slide 3) */}
      <section className="how-principles-section" id="principles">
        <div className="container">
          <div className="how-section-header fade-in">
            <span className="how-section-eyebrow">Pedagogical Foundation</span>
            <h2>Principles of Our Product</h2>
            <p>
              Built on proven educational psychology, human-in-the-loop control,
              and deep conversational insights.
            </p>
          </div>

          <div className="how-principles-grid">
            {/* Left Column: 5 Core Principles */}
            <div className="how-principles-list">
              {PRODUCT_PRINCIPLES.map((principle, index) => (
                <div
                  className="how-principle-card fade-in"
                  key={principle.title}
                >
                  <div className="how-principle-badge" aria-hidden="true">
                    0{index + 1}
                  </div>
                  <div className="how-principle-body">
                    <h3>{principle.title}</h3>
                    <div className="how-principle-subtitle">
                      {principle.subtitle}
                    </div>
                    <p>{principle.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Dashboard Interface Showcase */}
            <div className="how-principles-visual fade-in">
              <div className="how-dashboard-preview">
                <div className="how-dashboard-preview__header">
                  <span className="how-visual-tag">Continuous Insights</span>
                  <h3>Real-Time Comprehension Analytics</h3>
                  <p>
                    Educators see student mastery across units, active session
                    transcripts, and real-time comprehension trends without
                    waiting for test day.
                  </p>
                </div>
                <div className="how-dashboard-preview__image-box">
                  <img
                    src={asset('images/how-it-works-dashboard.webp')}
                    alt="LearnAI educator dashboard and lesson transcript analysis"
                    width={1228}
                    height={1254}
                    loading="lazy"
                    className="how-dashboard-preview__img"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bloom's 2 Sigma Foundation Strip */}
          <div className="how-bloom-strip fade-in">
            <div className="how-bloom-strip__icon" aria-hidden="true">
              {Icons.book('how-bloom-strip__svg')}
            </div>
            <div className="how-bloom-strip__content">
              <strong>Bloom&apos;s 2 Sigma Research Foundation:</strong>{' '}
              Bloom&apos;s 2 sigma problem refers to the educational phenomenon
              that the average student tutored one-to-one using mastery learning
              techniques performed two standard deviations better than students
              educated in a classroom environment.
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Credentials Strip */}
      <section className="credentials-strip">
        <div className="container credentials-inner">
          <div className="credentials-row">
            <span className="credential-item">{CREDENTIALS[0]}</span>
            <span className="credential-item">{CREDENTIALS[1]}</span>
          </div>
          <div className="credentials-row">
            <span className="credential-item">{CREDENTIALS[2]}</span>
            <span className="credential-item">{CREDENTIALS[3]}</span>
          </div>
        </div>
      </section>

      {/* Section 4: CTA */}
      <section className="section-cta">
        <div className="container cta-content fade-in">
          <h2>
            Ready to bring proactive AI tutoring
            <br />
            to your school?
          </h2>
          <a href={href('/contact')} className="btn btn--primary btn--cta">
            Get in Touch
          </a>
        </div>
      </section>
    </Layout>
  );
}
